import {
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  required,
  maxLength,
  minLength,
  SelectInput,
  NumberInput,
  useDataProvider,
  useRecordContext,
  NullableBooleanInput,
} from "react-admin";
import { Grid } from "@mui/material";
import { validateStart, validateEnd } from "utils/validations";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import { TaxTypes } from "utils/constants";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import Ibans from "components/Campaigns/components/Ibans";
import { specialistRequests } from "api/specialistApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import CustomTimeInput from "server/components/Fields/CustomTimeInput";
import RoomInput from "server/components/Fields/RoomInput";
import { administrationRequests } from "api/administrationApi";
import SpecializationOptionFields from "./Partials/SpecializationOptionFields";
import { useNotifyError } from "utils/useNotifyError";

const NAMES_MAX_LENGTH = 200;

const SaveForm = ({ isEdit = false }) => {
  const dataProvider = useDataProvider();
  const { setValue, control, getValues } = useFormContext();
  const record = useRecordContext();
  const notifyError = useNotifyError();
  const [specializationExamsIds, setSpecializationExamsIds] = useState([]);

  const [specializationIds, setSpecializationIds] = useState([]);
  const [selectedExamsIds, setSelectedExamsIds] = useState(record?.exams?.map(x => x.examId) || []);
  const [selectedDocuments, setSelectedDocuments] = useState(record?.documentTypes?.map(x => x.documentTypeId) || []);

  useEffect(() => {
    if (!specializationExamsIds?.length) return;

    const currentExams = getValues("exams") || [];

    const mergedExams = specializationExamsIds.map(id => {
      const existingExam = currentExams.find(x => x.examId === id);

      // ако вече съществува → пазим старите данни
      if (existingExam) {
        const date = existingExam?.slot?.date?.split("T")[0];
        const time = existingExam?.slot?.time;

        const normalizedTime = date && time && !time.includes("T") ? `${date}T${time}` : time || "";

        return {
          ...existingExam,
          slot: {
            ...existingExam.slot,
            time: normalizedTime,
          },
        };
      }

      // нов изпит
      return {
        examId: id,

        note: "",

        slot: {
          date: "",
          time: "",
          roomId: null,
        },
      };
    });

    setValue("exams", mergedExams, {
      shouldDirty: true,
    });
  }, [specializationExamsIds]);

  const handleDocumentsChange = Id => {
    if (selectedDocuments.includes(Id)) {
      setSelectedDocuments(selectedDocuments.filter(itemId => itemId !== Id));
    } else {
      setSelectedDocuments([...selectedDocuments, Id]);
    }
  };

  const validateStartDate = (value, allValues) => {
    return validateStart(value, allValues.registrationEndsOn, true);
  };

  const validateEndDate = (value, allValues) => {
    return validateEnd(value, allValues.registrationStartsOn, true);
  };
  const exams = useWatch({
    control,
    name: "exams",
  });
  const specializationOptions = useWatch({
    control,
    name: "specializationOptions",
  });

  useEffect(() => {
    const ids = specializationOptions?.map(item => item?.specializationId)?.filter(Boolean) || [];

    setSpecializationIds(ids);
  }, [specializationOptions]);

  useEffect(() => {
    const params = specializationIds.length ? specializationIds.map(id => `SpecializationIds=${id}`).join("&") : null;

    const fetchSpecializationExams = async () => {
      try {
        const res = await dataProvider.customRequest(
          specialistRequests.getSpecializationExamsIds(),
          null,
          "GET",
          params ? `?${params}` : null,
        );
        setSpecializationExamsIds(res.data);
      } catch (error) {
        notifyError(error);
      }
    };
    fetchSpecializationExams();
  }, [specializationIds, dataProvider, notifyError]);

  useEffect(() => {
    const validExams = exams?.filter(e => e?.examId != null) || [];
    const taxes = record?.taxes || getValues("taxes");
    const generatedTaxes = [
      {
        taxTypeId: TaxTypes.CandidateDocumentProcessing,
        price: taxes?.[0]?.price || null,
        examsCount: 1,
      },
      ...validExams?.map((tax, index) => ({
        taxTypeId: tax.taxTypeId || TaxTypes.CandidateExams,
        price: taxes?.[index + 1]?.price || null,
        examsCount: index + 1,
      })),
    ];

    setValue("taxes", generatedTaxes, {
      shouldDirty: true,
    });
  }, [exams, isEdit, setValue]);
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameBg"
          label="Име на български"
          validate={[required(), maxLength(NAMES_MAX_LENGTH), minLength(1)]}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameEn"
          label="Име на английски"
          validate={[maxLength(NAMES_MAX_LENGTH)]}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateTimeInput
          source="registrationStartsOn"
          label="Начало на регистрация"
          required
          validate={validateStartDate}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateTimeInput
          source="registrationEndsOn"
          label="Край на регистрация"
          required
          validate={validateEndDate}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="structuralUnitId"
          reference={administrationRequests.getStructuralUnitsSelect()}
          filter={{ filterByUser: true }}
        >
          <SelectInput
            label="Структурна единица"
            validate={[required()]}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ArrayInput
          source="specializationOptions"
          label="Специализации"
          validate={[required()]}
        >
          <SimpleFormIterator>
            <Grid
              container
              spacing={2}
            >
              <SpecializationOptionFields />
              <Grid
                item
                xs={12}
              >
                <Grid container>
                  <Grid
                    item
                    xs={12}
                  >
                    <BooleanInput
                      source="isEmployeesOnly"
                      label="Само за служители"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <BooleanInput
                      source="isPracticeTrainingProvidedByUniversity"
                      label="Практическо обучение от университета"
                      defaultValue={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </SimpleFormIterator>
        </ArrayInput>
        {specializationExamsIds.length > 0 ? (
          <ArrayInput
            source="exams"
            label="Изпити"
          >
            <SimpleFormIterator
              disableReordering
              disableAdd
              disableRemove
            >
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                >
                  <ReferenceInput
                    source="examId"
                    reference={administrationRequests.getExamsSelect()}
                    filter={{ ids: specializationExamsIds, ExcludeIds: selectedExamsIds }}
                  >
                    <AutocompleteInput
                      sx={{
                        pointerEvents: "none",
                        "& .MuiAutocomplete-popupIndicator": {
                          display: "none",
                        },
                      }}
                      optionText="name"
                      label="Изпит"
                      validate={required()}
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={3}
                >
                  <CustomDateInput
                    source="slot.date"
                    label="Дата на изпита"
                    required={false}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={3}
                >
                  <CustomTimeInput
                    source="slot.time"
                    label="Час на изпит"
                    required={false}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={3}
                >
                  <RoomInput source="slot.roomId" />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextInput
                    source="note"
                    label="Бележка"
                    defaultValue={""}
                  />
                </Grid>
              </Grid>
            </SimpleFormIterator>
          </ArrayInput>
        ) : null}
        <ArrayInput
          source="taxes"
          label="Такси"
          defaultValue={
            !isEdit
              ? [
                  {
                    taxTypeId: TaxTypes.CandidateDocumentProcessing,
                    price: null,
                  },
                ]
              : null
          }
        >
          <SimpleFormIterator
            disableReordering
            disableAdd
            disableRemove
          >
            <ReferenceInput
              source="taxTypeId"
              reference={administrationRequests.getTaxTypesSelect()}
            >
              <SelectInput
                sx={{
                  pointerEvents: "none",
                  "& .MuiSelect-icon": {
                    display: "none",
                  },
                }}
                label="Такса"
                validate={required()}
              />
            </ReferenceInput>
            <NumberInput
              source="price"
              label="Стойност"
              validate={required()}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ArrayInput
          source="documentTypes"
          label="Документи"
        >
          <SimpleFormIterator>
            <ReferenceInput
              source="documentTypeId"
              reference={administrationRequests.getPreferenceDocumentTypesSelect()}
              filter={{ ExcludeIds: selectedDocuments }}
              label=""
            >
              <SelectInput
                onChange={e => handleDocumentsChange(e.target.value)}
                optionText="name"
                label="Документ"
              />
            </ReferenceInput>

            <NullableBooleanInput
              label="Задължително"
              source="isRequired"
              nullLabel=""
              falseLabel="Не"
              trueLabel="Да"
            />
          </SimpleFormIterator>
        </ArrayInput>
        <Ibans />
      </Grid>
    </Grid>
  );
};

export default SaveForm;
